import { acceptHMRUpdate, defineStore } from 'pinia'
import { Ref, computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useQueryClient } from '@tanstack/vue-query'

import * as Sentry from '@sentry/vue'

import { User } from '../users/models/user'

import { useEventStore } from '../common/eventStore'

import { login, register, twoFactorChallenge } from '../auth/authService'
import {
  ERROR_TYPES,
  isApiError,
  isTransientApiError,
} from '../shared/api/apiError'
import { useAuthSiteStore } from './authSiteStore'
import { logOut, show } from './authUserService'

const FETCH_USER_ATTEMPTS = 3
const MAX_RETRY_DELAY_MS = 3000

const retryDelayMs = (error: unknown) => {
  const retryAfterMs =
    isApiError(error) && error.detail.type === ERROR_TYPES.RATE_LIMITED
      ? error.detail.retryAfterMs
      : undefined

  return Math.min(retryAfterMs ?? 1000, MAX_RETRY_DELAY_MS)
}

export const useAuthUserStore = defineStore('AuthUserStore', () => {
  const router = useRouter()
  const queryClient = useQueryClient()

  // Drop everything cached for the previous user (API data, sites, permissions, flags).
  // Called on sign-out and on every sign-in, since a session can also end by expiring.
  const authSiteStore = useAuthSiteStore()
  const resetSessionState = () => {
    queryClient.clear()
    authSiteStore.reset()
  }

  const isInitialised = ref(false)
  const isFetchingUser = ref(false)
  const user: Ref<User | null> = ref(null)
  const isAuthenticated = computed(() => !!user.value)

  // A caller that arrives while the user is being fetched waits for the same request (UNIT-15)
  let inFlightFetch: Promise<void> | null = null
  const fetchUser = async () => {
    if (isInitialised.value) {
      return
    }

    if (inFlightFetch) {
      return inFlightFetch
    }

    inFlightFetch = loadUser().finally(() => {
      inFlightFetch = null
    })

    return inFlightFetch
  }

  // Set when /api/user keeps failing for a reason other than the session (429, 5xx, network).
  // The user is then unknown rather than signed out, and auth stays uninitialised so the next
  // navigation asks again (E2E-R4-02).
  const isUnavailable = ref(false)

  const loadUser = async () => {
    isFetchingUser.value = true

    try {
      const result = await showWithRetry()

      user.value = result.user
      isUnavailable.value = false
      isInitialised.value = true
    } catch (error) {
      console.error(error)

      Sentry.captureException(error)

      user.value = null

      if (isTransientApiError(error)) {
        isUnavailable.value = true
      } else {
        isUnavailable.value = false
        isInitialised.value = true
      }
    } finally {
      isFetchingUser.value = false
    }
  }

  const showWithRetry = async () => {
    for (let attempt = 1; ; attempt++) {
      try {
        return await show({
          skipAuthRedirect: true,
        })
      } catch (error) {
        if (!isTransientApiError(error) || attempt >= FETCH_USER_ATTEMPTS) {
          throw error
        }

        await new Promise((resolve) => setTimeout(resolve, retryDelayMs(error)))
      }
    }
  }

  const isSignUpLoading = ref(false)
  const signUpError = ref<string | null>(null)
  const signUp = async ({
    name,
    email,
    password,
    passwordConfirmation,
  }: {
    name: string
    email: string
    password: string
    passwordConfirmation: string
  }) => {
    isSignUpLoading.value = true
    signUpError.value = null

    try {
      await register(name, email, password, passwordConfirmation)

      resetSessionState()
      isInitialised.value = false
      await fetchUser()

      redirectAfterRegistration()
    } catch (error: unknown) {
      signUpError.value = (error as Error).message
    } finally {
      isSignUpLoading.value = false
    }
  }

  const isSignInLoading = ref(false)
  const signInError = ref<string | null>(null)
  const signIn = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }) => {
    isSignInLoading.value = true
    signInError.value = null

    try {
      const response = await login(email, password, { skipAuthRedirect: true })

      if (response.two_factor) {
        router.push({ name: '/(guest)/two-factor' })
      } else {
        resetSessionState()
        isInitialised.value = false
        await fetchUser()

        redirectAfterLogin()
      }
    } catch (error: unknown) {
      signInError.value = (error as Error).message
    } finally {
      isSignInLoading.value = false
    }
  }

  const isSignInTwoFactorLoading = ref(false)
  const signInTwoFactor = async ({ code }: { code: string }) => {
    isSignInTwoFactorLoading.value = true

    try {
      await twoFactorChallenge(code, { skipAuthRedirect: true })

      resetSessionState()
      isInitialised.value = false
      await fetchUser()

      redirectAfterLogin()
    } finally {
      isSignInTwoFactorLoading.value = false
    }
  }

  const signOut = async () => {
    user.value = null
    isInitialised.value = true
    isUnavailable.value = false
    resetSessionState()

    await logOut({ skipAuthRedirect: true })

    await router.push({ name: '/(guest)/login' })
  }

  const redirectAfterLogin = async () => {
    router.push({ path: '/' })
  }

  const redirectAfterRegistration = async () => {
    router.push({ name: '/(guest)/org.new' })
  }

  const sessionExpired = ref(false)
  const setSessionExpired = () => {
    sessionExpired.value = true
  }
  const clearSessionExpired = () => {
    sessionExpired.value = false
  }

  const passwordExpired = ref(false)
  const setPasswordExpired = () => {
    passwordExpired.value = true
  }
  const clearPasswordExpired = () => {
    passwordExpired.value = false
  }

  const eventStore = useEventStore()
  const { showEmailVerificationToast } = eventStore

  watch(user, (newUser) => {
    if (newUser && !newUser.emailVerifiedAt) {
      showEmailVerificationToast()
    }
  })

  return {
    isInitialised,
    isFetchingUser,
    user,
    fetchUser,
    isAuthenticated,
    isUnavailable,

    isSignUpLoading,
    signUpError,
    signUp,

    isSignInLoading,
    signInError,
    isSignInTwoFactorLoading,
    signIn,
    signInTwoFactor,

    signOut,

    sessionExpired,
    setSessionExpired,
    clearSessionExpired,

    passwordExpired,
    setPasswordExpired,
    clearPasswordExpired,
  }
})

if (import.meta.hot !== null && import.meta.hot !== undefined) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthUserStore, import.meta.hot))
}
